import * as fabric from "fabric";
import React, { useRef } from "react";
import { useAppDispatch } from "../../../../hook/redux";
import store from "../../../../store";
import { backgroundCreation } from "../../../../utils/backgroundCreation";
import { createCanvasImage } from "../../../Canvas/createCanvasImage";
import { handleDisplayToolbar } from "../../../utils";


/**
 * Handles the processing and addition of a file (image) to a Fabric.js canvas.
 * This function supports both Base64 string inputs and `Blob`/`File` objects,
 * processes them into Fabric.js image objects, and updates the canvas state accordingly.
 *
 * @param {string | Blob} file - The file to be processed, which can either be a Base64 string or a `Blob`/`File`
 *   object.
 * @param dispatch - The Redux dispatch function for updating state changes.
 * @param {Object} fabricCanvasInstanceRef - A React reference to the Fabric.js canvas instance.
 * @param {Object} fabricImage - The fabric.Image instance or placeholder containing meta information about the image.
 * @returns {Promise<fabric.Image>} - A promise that resolves with the Fabric.js image instance created and added to
 *   the canvas.
 */
export const handleFileChange = async (
  file: string | Blob,
  dispatch: Function,
  fabricCanvasInstanceRef: fabric.Canvas,
  fabricImage: fabric.Image
): Promise<fabric.Image> => {
  const getBackground = () => store.getState().editor.selectedBackground;
  const getScale = () => store.getState().editor.scale;
  const getSelectedLayout = () => store.getState().editor.selectedLayout;
  const getOrientation = () => store.getState().editor.currentData.paramGeneral.orient;
  const getCurrentData = () => store.getState().editor.currentData;
  const getLayouts = () => store.getState().editor.layouts;

  const selectedBackground = getBackground();
  const scale = getScale();
  const selectedLayout = getSelectedLayout();
  const orientation = getOrientation();
  const currentData = getCurrentData();
  const layouts = getLayouts();

  const layout = layouts.find( ( layout ) => layout.id === selectedLayout );
  const layoutData = currentData.layoutData.find( ( data ) => data.id === selectedLayout );
  const item = layout?.elements.find( ( element ) => element.imgIdx === fabricImage?.idx );

  return new Promise( ( resolve, reject ) => {
    try {
      if ( !file ) {
        return reject( new Error( "File is undefined or invalid." ) );
      }

      dispatch( {
        type: "editor/updateIsLoading",
        payload: true,
      } );

      const processAndAddImage = async ( imageUrl: string ) => {
        const existingObjects = fabricCanvasInstanceRef?.current?._objects.filter(
          ( obj ) => obj?.idx !== fabricImage?.idx && (obj.type !== "rect" || obj.placeholder === true)
        );

        fabricCanvasInstanceRef.current?.clear();
        await backgroundCreation(
          selectedBackground,
          orientation,
          fabricCanvasInstanceRef.current,
          scale
        );

        const uploadedImage = await createCanvasImage(
          imageUrl,
          item,
          scale,
          layoutData?.borderRadius,
          fabricCanvasInstanceRef,
          dispatch,
          selectedLayout,
          fabricImage.idx,
          null,
          false
        );

        existingObjects &&
        existingObjects.forEach( ( obj ) => fabricCanvasInstanceRef.current?.add( obj ) );

        // Redessiner le canevas
        fabricCanvasInstanceRef.current?.requestRenderAll();

        if ( typeof file !== "string" ) {
          uploadedImage.on( "mousedown", ( event ) => {
            event.e.stopPropagation();
            handleDisplayToolbar( dispatch, "image", uploadedImage.idx );
          } );

          const targetObject = fabricCanvasInstanceRef.current._objects.find(
            ( obj ) => obj.idx === uploadedImage.idx && obj.type === "image"
          );

          if ( targetObject ) {
            fabricCanvasInstanceRef.current.setActiveObject( targetObject ); // Définit comme l'objet actif
          }

          fabricCanvasInstanceRef.current?.requestRenderAll();
          handleDisplayToolbar( dispatch, "image", targetObject.idx );
        }

        const placeHolderObjects = fabricCanvasInstanceRef.current && fabricCanvasInstanceRef.current._objects.some((object) => object.type == "image" && object.placeholderImage)
        dispatch({
          type: "editor/updateIsExportable",
          payload: !placeHolderObjects
        });


        resolve( uploadedImage );
        
      };

      if ( typeof file === "string" && file.startsWith( "data:image/" ) ) {
        processAndAddImage( file );

      } else if ( file instanceof Blob && file.type.startsWith( "image/" ) ) {
        const reader = new FileReader();

        reader.onload = async ( event ) => {
          const imageUrl = event.target?.result as string;
          await processAndAddImage( imageUrl );
        };

        reader.onerror = () => {
          reject( new Error( "Error reading file." ) );
        };

        reader.readAsDataURL( file );
      } else {
        reject( new Error( "Unsupported file type." ) );
      }
    } catch ( error ) {
      reject( error );
    } finally {
      dispatch( {
        type: "editor/updateIsLoading",
        payload: false,
      } );
    }
  } );
};

const UploadSelector = ( { object, canvas } ) => {

  const fileInputRef = useRef<HTMLInputElement>( null );
  const dispatch = useAppDispatch();

  return (
    <div className="toolbar-button" data-test={ "image-upload" }>
      <label htmlFor="file-upload">
        <i className="fas fa-upload" style={ { cursor: "pointer" } }></i>
        <input
          id="file-upload"
          type="file"
          onChange={ ( e ) => handleFileChange( e.target.files?.[ 0 ], dispatch, canvas, object ) }
          accept=".jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif"
          style={ { display: "none" } }
          ref={ fileInputRef }
        />
      </label>
    </div>
  );
};

export default UploadSelector;